<template>
  <div class="filters-main-container">
    <div class="btn-bdg-container row">
      <div class="col-md-9">
        <button class="btn-toggle-pan btn white-blue" @click="slidePan">
          <i class="fas fa-filter"></i>
          <span>{{ $t('_filter.filtering') }}</span>
        </button>

        <span class="badges">
          <b-badge v-for="badge in badges" :title="badge.length > 15 ? badge : ''"
                   v-b-tooltip="{ container:'.bookings', placement: 'bottom'}"
          >
            {{ badge |truncate(15) }}
          </b-badge>
        </span>
      </div>

      <div class="col-md-3 text-md-right custom-grid">
        <b-button class="btn white-blue reset" @click="reset"
                  v-if="!display && badges.length"
        >
          {{ resetFiltersBtnText }}
        </b-button>
      </div>
    </div>

    <section class="filters-board full-vh slide-right"
             id="bookings-filters" ref="filtersPan"
    >
      <header class="board-header">
        <p class="header-components v-align-between">
          <span>{{ $t('_filter.filtering') }}</span>
          <span @click="slidePan"><i class="fas fa-times"></i></span>
        </p>

        <hr>

        <p class="v-align-between">
          <b-button class="btn orange-white btn-validate" @click="emitChanges">
            {{ $t('_validate') }}
          </b-button>

          <b-button class="btn-as-link" @click="reset">
            {{ $t('_reset_all') }}
          </b-button>
        </p>
      </header>

      <div id="selection" class="filters-card">
        <div v-for="(filter, type, index) in filters" v-if="filter.enabled" :key="type">
          <div class="card-header v-align-between" v-b-toggle="type">
            <span class="icon-inner title">
             {{ filter.title || $t(`booking.filters.title.${type}`) }}
            </span>

            <fa :fa-class="`${filter.visible ? 'minus rotation-right' : 'plus rotation-left'}`" />
          </div>

          <b-collapse :id="type" role="tabpanel" accordion="filters" class="card-body"
                      :visible="!index" @input="toggleOpenedComponent(type, $event)"
          >
            <component :is="type" :name="type" :ref="type"
                       v-bind="filter.props" @update="update($event)"
            />
          </b-collapse>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import CommonApi from '@/api/commonApi'
import formatter from '@/components/global/mixins/formatterMethods'
import { getStatusTranslation } from '@/utils/bookingStatus'
import fa from '@/components/global/icon/fontAwesome'
import bookingRef from "./bookingRef"
import type from "./type";
import dates from "./dates";
import destination from "./destination";
import traveller from "./traveller";
import status from "./status";
import ref from "./ref";
import orderNumber from "./orderNumber";
import viewPort from '@/conf/viewPort'

export default {
  components: {
    fa,
    bookingRef,
    type,
    dates,
    destination,
    traveller,
    status,
    ref,
    orderNumber
  },

  mixins: [formatter, viewPort],

  props: {
    isManager: Boolean
  },

  data () {
    return {
      commonApi: new CommonApi(),
      filters: {
        bookingRef: {
          props: {
            options: []
          },
          visible: false,
          enabled: true
        },
        orderNumber: {
          props: {
            options: []
          },
          visible: false,
          enabled: this.orderNumber().enabled,
          title: this.orderNumber().title
        },
        type: {
          props: {
            options: [
              { value: 'online', text: this.$t('_online') },
              { value: 'offline', text: this.$t('_offline') }
            ]
          },
          visible: false,
          enabled: true
        },
        dates: {
          visible: false,
          enabled: true
        },
        destination: {
          props: {
            options: {
              city: {
                options: []
              },
              hotel: {
                options: []
              },
            }
          },
          visible: false,
          enabled: true
        },
        traveller: {
          props: {
            options: []
          },
          visible: false,
          enabled: true
        },
        status: {
          props: {
            options: []
          },
          visible: false,
          enabled: true
        },
        ref: {
          props: {
            options: []
          },
          visible: false,
          enabled: true
        }
      },
      selected: {},
      display: false,
      badges: [],
      cardOpen: true
    }
  },

  created () {
    this.initOptions()
  },

  mounted () {
    this.reset() // init selected
  },

  methods: {
    toggleOpenedComponent (type, visibility) {
      this.filters[type].visible = visibility

      if (type === 'dates' && visibility) {
        for (let date of Object.entries(this.$refs.dates[0].$refs)) {
          date[1].$refs.pika.show()
        }
      }
    },

    slidePan () {
      this.$refs.filtersPan.classList.toggle('slide-left')
      this.display = this.$refs.filtersPan.classList.contains('slide-left')
    },

    initOptions () {
      this.commonApi.reservationListFilterOptions()
        .then(response => {
          for (let ref of response.data.data.bookingRefs) {
            this.filters.bookingRef.props.options.push({
              value: ref,
              text: ref
            })
          }

          for (let city of response.data.data.cities) {
            this.filters.destination.props.options.city.options.push({
              value: city,
              text: city
            })
          }

          for (let [id, hotel] of Object.entries(response.data.data.hotels)) {
            this.filters.destination.props.options.hotel.options.push({
              value: id,
              text: this.formatHotelName(hotel)
            })
          }

          for (let traveller of response.data.data.travellers) {
            this.filters.traveller.props.options.push({
              value: traveller,
              text: `${traveller.firstName} ${traveller.lastName}`
            })
          }

          for (let status of response.data.data.statuses) {
            this.filters.status.props.options.push({
              value: status,
              text: getStatusTranslation(status, this.isManager)
            })
          }

          for (let ref of response.data.data.refs) {
            this.filters.ref.props.options.push({
              value: ref,
              text: ref
            })
          }

          for (let orderNumber of response.data.data.orderNumbers) {
            this.filters.orderNumber.props.options.push({
              value: orderNumber,
              text: orderNumber
            })
          }
        })
    },

    reset () {
      for (let ref of Object.entries(this.$refs)) {
        let name = ref[0]

        if (name !== 'filtersPan') ref[1][0].reset()
      }

      this.emitChanges(false)
    },

    update (data) {
      this.selected[data.name] = data.value
    },

    emitChanges (slidePan = true) {
      this.$emit('apply', this.selected)
      this.updateBadges()
      if (slidePan) this.slidePan()
    },

    updateBadges () {
      let ret = []

      for (let [key, filter] of Object.entries(this.selected)) {
        switch (key) {
          case 'bookingRef':
          case 'type':
          case 'orderNumber':
            if (filter && filter.text) {
              ret.push(filter.text)
            }
            break
          case 'dates':
            if (filter.from) ret.push(`> ${this.formatDate(filter.from)}`)
            if (filter.to) ret.push(`< ${this.formatDate(filter.to)}`)
            break
          case 'destination':
            for (let item of filter.city) {
              ret.push(item.text)
            }

            for (let item of filter.hotel) {
              ret.push(item.text)
            }
            break
          case 'traveller':
          case 'status':
          case 'ref':
          default:
            for (let item of filter) {
              ret.push(item.text)
            }
            break
        }
      }

      this.badges = ret
    },

    orderNumber () {
      let cieStore = this.$store.getters['security/company']

      let ret = {
        enabled: false,
        title: null
      }

      if (cieStore.orderNumber !== null && cieStore.orderNumber.required) {
        ret.enabled = true
        ret.title = cieStore.orderNumber.label
      }

      return ret
    }
  },

  computed: {
    resetFiltersBtnText () {
      return (this.isViewPortMobileLarge || this.isViewPortDesktop) ?
        this.$t('_reset_filters') :
        this.$t('_delete')
    }
  }
}
</script>
